import { InjectionToken } from '@angular/core';

import { FeatureFlag } from '@recruitee/feature-flags/data';

export enum AnalyticsViewContext {
  Candidates = 'candidates',
  CandidateProfile = 'candidate_profile',
  CandidateProfileSidebar = 'candidate_profile_sidebar',
  Offer = 'offer',
  TalentPool = 'talent_pool',
  TalentPools = 'talent_pools',
  Notes = 'notes',
  OpenQuestionsTemplates = 'open_questions_templates',
  ProfileFields = 'profile_fields',
  Settings = 'settings',
  EmailTemplates = 'email_templates',
  PipelineTemplates = 'pipelines_templates',
  DisqualifyReasons = 'disqualify_reasons',
  AutomatedActions = 'automated_actions',
  EmailForm = 'email_form',
  ScheduleDialog = 'schedule_dialog',
  Mailbox = 'mailbox',
  Dashboard = 'dashboard',
  NewSchedulerDialog = 'new_scheduler_dialog',
  Reports = 'reports',
  Pipeline = 'pipeline',
  Academy = 'academy',
  Jobs = 'jobs',
  JobEditor = 'job_editor',
  Calendar = 'calendar',
  Events = 'events',
  OfferPipeline = 'offer_pipeline',
  OfferFilters = 'offer_filters',
  Billing = 'billing',
  HeaderMenu = 'header_menu',
  FAB = 'FAB',
  TrialCenter = 'trial_center',
  NewsFeed = 'news_feed',
  Requisition = 'requisition',
  Requisitions = 'requisitions',
  OffersSelectDialog = 'offers_select_dialog',
}

export enum AnalyticsMode {
  Single = 'single',
  Bulk = 'bulk',
}

export type PageTrackParamsSanitizerFn = (
  name: string,
  category: string,
  params: AnalyticsProps,
) => AnalyticsProps;

const noopPageTrackParamsSanitizer: PageTrackParamsSanitizerFn = (
  name: string,
  category: string,
  params: AnalyticsProps,
) => params;

export class AnalyticsConfig {
  private readonly defaultSegmentOptions: SegmentClientOptions = {};

  constructor(
    public opts: {
      segmentKey?: string;
      segmentOptions?: SegmentClientOptions;
      debug?: boolean;
      disable?: boolean;
      waitForIdentify?: boolean;
      trackRouteChange: boolean;
      requiredFeatureFlag?: FeatureFlag;
      pageTrackParamsSanitizer?: PageTrackParamsSanitizerFn;
    },
  ) {}

  public get pageTrackParamsSanitizer(): PageTrackParamsSanitizerFn {
    return this.opts.pageTrackParamsSanitizer ?? noopPageTrackParamsSanitizer;
  }

  public get requiredFeatureFlag(): FeatureFlag | false {
    const { requiredFeatureFlag } = this.opts;
    return requiredFeatureFlag !== undefined ? requiredFeatureFlag : false;
  }

  public get isAvailable(): boolean {
    return !this.opts.disable && this.opts.segmentKey !== undefined;
  }

  public get isDebug(): boolean {
    return this.opts.debug === true;
  }

  public get key(): string {
    return this.opts.segmentKey!;
  }

  public get options(): SegmentClientOptions {
    return this.opts.segmentOptions || this.defaultSegmentOptions;
  }

  public get trackRoutes() {
    return this.opts.trackRouteChange || false;
  }

  public get waitForIdentify() {
    return this.opts.waitForIdentify || false;
  }
}

export type AnalyticsProps<T = any> = Record<
  keyof T,
  string | number | boolean | Array<string | number | boolean>
>;

export interface SegmentAPIMethods {
  alias: (id: number) => void;
  track: (eventName: string, eventProps?: AnalyticsProps, opts?: SegmentClientOptions) => void;
  identify: (
    userId?: number,
    traits?: Record<string, string | number>,
    options?: SegmentClientOptions,
    callback?: Function,
  ) => void;
  page: (
    category?: string,
    name?: string,
    properties?: AnalyticsProps,
    options?: SegmentClientOptions,
  ) => void;
  reset: () => void;
}

export interface SegmentClientOptions {
  integrations?: {
    // Destination flags are case sensitive and match the destination’s name in the docs (i.e. “AdLearn Open Platform”, “awe.sm”, “MailChimp”, etc.).
    // https://segment.com/docs/connections/destinations/catalog/index-all/
    All: boolean;
    [key: string]: boolean;
  };
}

export type AnalyticsEventDescriptor = {
  method: keyof SegmentAPIMethods;
  data: any;
  options: SegmentClientOptions;
};

export interface AnalyticsRouteData {
  analytics?: {
    category: string;
    name: string;
    filter?: (params: Record<any, any>) => boolean;
  };
}

export abstract class Analytics {
  public abstract alias(id: number): void;

  public abstract track(
    eventName: string,
    eventProps?: Record<string, boolean | string | number | undefined | null | string[]>,
    opts?: any,
  ): void;

  public abstract identify(
    userId?: number | string,
    traits?: Record<string, string | number>,
    options?: any,
  ): void;

  public abstract page(category?: string, name?: string, properties?: any, options?: any): void;
}

export const ANALYTICS_DISABLED = new InjectionToken<boolean>('Analytics disabled');
export const ANALYTICS_VIEW_CONTEXT = new InjectionToken<AnalyticsViewContext[]>(
  'Analytics view context',
);
export const ANALYTICS_VIEW_CONTEXT_LEAF = new InjectionToken<AnalyticsViewContext[]>(
  'Analytics context (leaf)',
);
